import { CellValueChangedEvent, RowNode } from '@ag-grid-community/core'
import { HttpParams } from '@angular/common/http'
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { FormControl, ValidationErrors } from '@angular/forms'
import {
  RootState,
  selectActiveSession,
  selectActiveSessionIdentifier,
  selectActiveSessionReconDate,
  selectActiveUserId,
  selectActiveWindowDate,
  setCommandsMetrics,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import {
  NgbActiveModal,
  NgbModal,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { ToastService } from '@northfork/ng-basics'
import { omit } from 'lodash-es'
import { Observable, of, Subject } from 'rxjs'
import {
  catchError,
  debounceTime,
  retry,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators'
import { MvtBasesInOut } from '../../../model/mvt-basis-in-out.model'
import { Basis } from '../../../model/mvt-basis.model'
import {
  AccountDetails,
  CombinedAccountDetails,
} from '../../../model/single-transaction/account-details.model'
import { CreateEditTransactionRequest } from '../../../model/single-transaction/create-edit-transaction.model'
import {
  DirectTransfer,
  TransferPositionData,
} from '../../../model/single-transaction/direct-transfer/direct-transfer-transaction.model'
import { MvtData } from '../../../model/single-transaction/market-value-transfer.model'
import { Security } from '../../../model/security.model'
import {
  ExchangePositionData,
  TransactionModalInput,
  TransactionModalMode,
} from '../../../model/single-transaction/popup-input.model'
import { SleeveSimpleAccounts } from '../../../model/single-transaction/sleeve-simple-accounts.model'
import { TransactionNote } from '../../../model/single-transaction/transaction-note'
import { DirectTransferValidationView } from '../../../model/single-transaction/validation-view.model'
import {
  TranCategory,
  TranCategoryUrl,
} from '../../../model/transaction-category.model'
import {
  TransactionTypeEnum,
  TransactionTypeKey,
} from '../../../model/transaction-type.model'
import {
  ErrorSeverity,
  ValidationInfo,
} from '../../../model/validation-response.model'
import { CreateEditTransactionService } from '../../../services/create-edit-transaction.service'
import { ModalFocusService } from '../../../services/modal-focus.service'
import {
  activateDialog,
  createMetadataDirectTransfer,
  getDefaultUnknownCounterParty,
  getFieldValidatorFormatter,
  isDialogComponentInActive,
  singleDialogMetricsDetail,
  SINGLE_DIALOG_EVENT_NAME,
  SINGLE_DIALOG_VALIDATION_EVENT_NAME,
} from '../../../util/common-utils'
import { TransactionConstants } from '../../../util/constants'
import { getDefaultCurrency } from '../../../util/currency-utils'
import { isEditModal, modalModeToCreate } from '../../../util/popup-input-util'
import { getRequestModeFromModalMode } from '../../../util/validation-response-util'
import { DirectTransferSaveConfirmationModalComponent } from './direct-transfer-confirm-button-modal/direct-transfer-confirm-button-modal'
import { ExchangeInOutFormComponent } from './exchange-in-out-form/exchange-in-out-form.component'
import { TransferInOutFormComponent } from './transfer-in-out-form/transfer-in-out-form.component'

export enum ExchangeType {
  CUSIP_EXCHANGE = 'CUSIP_EXCHANGE',
  CONVERSION = 'CONVERSION',
}

type TransferType = {
  name: string
  id: number
  tranType: TransactionTypeKey
}

/**
 * Modal controller component. Responsible for 3 tasks
 *
 * 1. Requesting the transaction from the backend if we're editing it
 * 2. Switching between transaction types when creating a new transaction
 * 3. Converting the form data into a format readable by the backend
 */
@Component({
  selector: 'hs-direct-transfer',
  templateUrl: './direct-transfer.component.html',
  styleUrls: ['./direct-transfer.component.scss'],
})
export class DirectTransferComponent implements OnInit, OnDestroy {
  // True while an aggregate account is selected but no concrete sub-account has been
  // picked yet. Passed to the sub-forms so they defer validation and flag edited fields.
  aggregateAccountUnresolved = false
  _transInput?: TransactionModalInput

  @ViewChild('transfer')
  readonly transferComponent?: TransferInOutFormComponent

  @ViewChild('exchange')
  readonly exchangeComponent?: ExchangeInOutFormComponent

  @Input() set transInput(transInput: TransactionModalInput | undefined) {
    // If no transactionId is supplied, exit edit mode
    if (
      transInput &&
      isEditModal(transInput) &&
      transInput.transactionId == null
    ) {
      transInput.modalMode = modalModeToCreate(transInput.modalMode)
    } else {
      this.editMode = isEditModal(transInput)
    }
    this._transInput = transInput
  }

  get transInput() {
    return this._transInput
  }

  editMode = false
  readonly transferOut: TransferType = {
    id: 1,
    name: 'Transfer in/out',
    tranType: 'TRANSFER_OUT',
  }
  readonly exchangeOut: TransferType = {
    id: 2,
    name: 'Exchange in/out',
    tranType: 'EXCHANGE_OUT',
  }

  transferTypes = [this.transferOut, this.exchangeOut]
  clearTransaction = new FormControl(true)
  transferType = new FormControl(this.transferOut)
  positionData?: ExchangePositionData
  transferPositionData?: TransferPositionData
  accountDetails: AccountDetails | undefined
  sleeveSimpleAccount: SleeveSimpleAccounts[] = []
  loadedComponent?: TransferInOutFormComponent | ExchangeInOutFormComponent
  transaction?: DirectTransfer
  tranNote?: TransactionNote
  cusip = ''
  saveButtonWarning: string | null = null
  genericWarnings = new Set<string>()
  originalTransaction?: DirectTransfer
  mvtDataMap?: { [key: number]: MvtData }
  conversionChecked = false
  sourceAccountId?: number
  transactionLoaded = false
  rowNodeMap = new Map<string, RowNode>()
  isDataValid = false
  isCellEditing = false
  accountLoaded = true
  reconDate?: Date
  windowDate?: Date
  sessionId?: string
  currentSecurityId?: number = getDefaultCurrency().cashSecurityId
  newSecurityId?: number
  executionId?: number
  originalCusip?: string
  externalAccountId?: number
  inOutMvtBases?: MvtBasesInOut
  selectedBasis: Basis[] = []
  note = new FormControl('')
  noteWarning: ValidationErrors | null = null
  getFieldValidatorFormatter = getFieldValidatorFormatter
  destinationAccountDetail: AccountDetails | undefined
  isSourceCollateral = false
  isDestinationCollateral = false
  security?: Security

  forbiddenErrors = new FormControl('')

  isValidating = false
  compId = 'directTransfer-' + Math.random()
  activeInstances: NgbModalRef[] = []
  loadingText = TransactionConstants.LOAD_TRANSACTION
  errorText = ''
  errorDetails: unknown
  errorHeader: string | undefined
  title = 'Create Direct Transfer'
  clickAsync = of(true).pipe(
    switchMap(() => this.onSubmit()),
    tap(response => {
      if (response !== 'error') {
        this.activeModal.close()
      }
    }),
  )

  private userId?: number

  private readonly destroySubject$ = new Subject<void>()

  constructor(
    private readonly toastService: ToastService,
    readonly activeModal: NgbActiveModal,
    private readonly store: Store<RootState>,
    private readonly createEditTransactionService: CreateEditTransactionService,
    private readonly modalService: NgbModal,
    private readonly elRef: ElementRef,
    private readonly modalFocusService: ModalFocusService,
    private readonly telemetryService: TelemetryService,
  ) {
    this.modalService.activeInstances
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(activeInstances => {
        this.activeInstances = activeInstances
      })
    this.store
      .select(selectActiveSessionIdentifier)
      .pipe(take(1))
      .subscribe((sessionId?: string) => {
        this.sessionId = sessionId
        // The session changed, so this modal should be closed.
        if (this.sessionId !== sessionId) {
          this.activeModal.dismiss()
        }
      })
    this.store
      .select(selectActiveWindowDate)
      .pipe(take(1))
      .subscribe(windowDate => {
        this.windowDate = windowDate
      })

    this.store
      .select(selectActiveSession)
      .pipe(take(1))
      .subscribe(session => {
        this.sourceAccountId = session?.loadedAccountIds[0]
      })

    this.store
      .select(selectActiveSessionReconDate)
      .pipe(take(1))
      .subscribe((reconDate?: Date) => {
        this.reconDate = reconDate
      })
    this.store
      .select(selectActiveUserId)
      .pipe(take(1))
      .subscribe(userId => {
        this.userId = userId
      })
  }

  ngOnInit(): void {
    const startTime = Date.now()
    if (this.sessionId == null) {
      return
    }
    activateDialog(this.activeInstances, this.compId, this.elRef)

    this.modalFocusService
      .outsideFocus(this.elRef)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => this.disableGridFocus())

    const positionData = this.transInput?.positionData
    if (
      positionData &&
      this.transInput?.modalMode == TransactionModalMode.CreateFromPosition
    ) {
      if ('rate' in positionData) {
        this.transferType.patchValue(this.exchangeOut)
        this.positionData = positionData
      }
      if ('sessionReconDate' in positionData) {
        this.transferType.patchValue(this.transferOut)
        this.transferPositionData = positionData
      }
    }
    this.note.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      this.validate()
    })
    this.clearTransaction.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      this.validate()
    })

    this.editMode = isEditModal(this.transInput) ?? false
    if (this.editMode) {
      this.title = this.transInput?.readOnly
        ? 'View Direct Transfer'
        : 'Edit Direct Transfer'
      this.loadClearwaterTransaction(this.sessionId)
    } else {
      this.loadAccountDetails()
    }
    const now = Date.now()
    const type = singleDialogMetricsDetail(this.transInput)
    this.telemetryService.emitCustomEvent(SINGLE_DIALOG_EVENT_NAME, {
      dialogName: 'Direct-Transfer-' + type,
      durationMs: now - startTime,
    })
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  loadClearwaterTransaction(sessionId: string) {
    const transactionId = this.transInput?.transactionId
    const securityId = this.transInput?.securityId
    const accountId = this.sourceAccountId
    if (transactionId == null || securityId == null || accountId == null) {
      return
    }
    const tranUrl = TranCategoryUrl.TRANSFER
    this.transactionLoaded = false
    const params = new HttpParams()
      .set('accountId', accountId.toString())
      .set('securityId', securityId.toString())
    const transactionDetailPromise = this.createEditTransactionService
      .loadTransactionDetail(sessionId, transactionId, params, tranUrl)
      .toPromise()
    let destinationAccountDetailPromise
    let isCollateralBrokerPromise
    if (this.transInput?.tranLinkInfo?.destAccountId) {
      destinationAccountDetailPromise = this.createEditTransactionService
        .loadAccountDetails(this.transInput?.tranLinkInfo?.destAccountId)
        .toPromise()
      isCollateralBrokerPromise = this.createEditTransactionService
        .isCollateralBroker([
          accountId,
          this.transInput?.tranLinkInfo?.destAccountId,
        ])
        .toPromise()
    }
    Promise.all([
      transactionDetailPromise,
      destinationAccountDetailPromise,
      isCollateralBrokerPromise,
    ])
      .then(
        async ([trans, destinationAccountDetail, isCollateralBrokerMap]) => {
          const transaction = trans.transactionData as DirectTransfer

          this.tranNote = trans.note
          this.transactionLoaded = true
          this.mvtDataMap = trans.mvtDataMap
          this.originalTransaction = transaction
          this.originalTransaction.units = Math.abs(
            this.originalTransaction.units,
          )
          this.accountDetails = trans.accountDetails
          if (trans.accountDetails?.isAggregate) {
            await this.loadAndMapAccountDetailsForSleeve(transaction.accountId)
          }
          this.transaction = transaction
          if (trans.securityMasterData) {
            this.cusip = trans.securityMasterData.cusip
            this.security = {
              securityMasterData: trans.securityMasterData,
              securityTypeData: trans.securityTypeData,
            } as Security
          }
          this.note.setValue(this.tranNote?.note ?? '')
          this.clearTransaction.patchValue(
            transaction.clearedDate ? true : false,
          )
          this.destinationAccountDetail = destinationAccountDetail
          if (isCollateralBrokerMap) {
            this.isDestinationCollateral =
              isCollateralBrokerMap[
                this.transInput?.tranLinkInfo?.destAccountId ?? 0
              ]
            this.isSourceCollateral = isCollateralBrokerMap[accountId]
          }
        },
      )
      .catch(err => {
        this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
        this.errorDetails = err.error
      })
  }

  /** @internal */
  loadAccountDetails() {
    if (this.sourceAccountId) {
      this.createEditTransactionService
        .loadAccountDetails(this.sourceAccountId)
        .subscribe(
          accountDetails => {
            this.accountDetails = accountDetails
            if (accountDetails.isAggregate) {
              this.aggregateAccountUnresolved = true
              this.fetchSleeveSimples(this.accountDetails?.accountId)
              this.resetAggAccountDetails()
            } else {
              this.createEditTransactionService
                .isCollateralBroker([this.sourceAccountId ?? 0])
                .subscribe(
                  isCollateralBrokerMap => {
                    this.isSourceCollateral =
                      isCollateralBrokerMap[this.sourceAccountId ?? 0]
                  },
                  err => {
                    this.errorHeader =
                      'Error Loading if account is collteral or not'
                    this.errorDetails = err.error
                  },
                )
            }
            this.validate()
          },
          err => {
            this.errorHeader = TransactionConstants.ERROR_LOADING_ACCOUNT
            this.errorDetails = err.error
          },
        )
    }
    this.transactionLoaded = true
  }

  /** @internal */
  createTranCommand() {
    const cusip = this.rowNodeMap.get('oldCusip')
      ? this.rowNodeMap.get('oldCusip')?.data.value
      : this.rowNodeMap.get('newCusip')?.data.value

    if (isEditModal(this.transInput)) {
      this.currentSecurityId = this.originalTransaction?.securityId
    }
    const destAccountId = this.getDestAccountId()
    let unclearTransaction = false
    if (this.originalTransaction?.clearedDate && !this.clearTransaction.value) {
      unclearTransaction = true
    }
    const tranCommand: CreateEditTransactionRequest = {
      transaction: this.prepareTransactionData(false),
      tranCategory: TranCategory.DIRECT_TRANSFER,
      metadata: createMetadataDirectTransfer(
        this.reconDate ?? new Date(),
        cusip,
        destAccountId,
        Number(this.rowNodeMap.get('rate')?.data.value),
        1.0,
        this.newSecurityId ?? this.currentSecurityId ?? -1,
        this.selectedBasis.map(b => omit(b, 'checked')) as Basis[],
        this.rowNodeMap.get('exchangeInOutType')?.data.value,
        unclearTransaction,
        this.inOutMvtBases,
      ),
    }

    if (this.note.value != '') {
      tranCommand.transactionNote = { newValue: this.note.value }
    }

    return tranCommand
  }

  private getDestAccountId() {
    let destAccountId = Number(this.rowNodeMap.get('destAccountId')?.data.value)
    const tranType =
      TransactionTypeEnum[
        this.transferType.value.tranType as TransactionTypeKey
      ]
    if (
      tranType === TransactionTypeEnum.EXCHANGE_IN ||
      tranType === TransactionTypeEnum.EXCHANGE_OUT
    ) {
      destAccountId = this.accountDetails?.accountId ?? 0
    }
    return destAccountId
  }

  validateAndSave() {
    if (!this.isDataValid || !this.sessionId) {
      return
    }
    return this.saveButtonWarning
      ? this.displaySaveButtonWarning(this.saveButtonWarning)
      : this.clickAsync.subscribe()
  }

  displaySaveButtonWarning(warningMsg: string | undefined) {
    const modalRef = this.modalService.open(
      DirectTransferSaveConfirmationModalComponent,
      { keyboard: false },
    )
    modalRef.componentInstance.saveModalInput = warningMsg
    modalRef.result.then((confirmed: boolean) => {
      return confirmed ? this.clickAsync.subscribe() : ''
    })
  }

  onSubmit() {
    if (!this.isDataValid || !this.sessionId) {
      return of(null)
    }
    this.store.dispatch(
      setCommandsMetrics({
        command: 'directTransfer',
        time: Date.now(),
      }),
    )

    const tranCommand = this.createTranCommand()

    let submitObservable: Observable<unknown>
    let commandDescription: string

    if (isEditModal(this.transInput)) {
      submitObservable = this.editCWTransaction(this.sessionId, tranCommand)
      commandDescription = 'Clearwater Transaction edit'
    } else {
      submitObservable = this.createCWTransaction(this.sessionId, tranCommand)
      commandDescription = 'Clearwater Transaction create '
    }
    return submitObservable.pipe(
      catchError(() => {
        this.toastService.error(
          `${commandDescription} command submission failed.`,
        )
        return of('error')
      }),
      tap(response => {
        if (response !== 'error') {
          this.toastService.success(
            `${commandDescription} command successfully submitted.`,
          )
        }
      }),
    )
  }

  /** @internal */
  editCWTransaction(
    sessionId: string,
    tranCommand: CreateEditTransactionRequest,
  ) {
    const transactionId = this.originalTransaction?.transactionId
    if (transactionId == null) {
      return of(null)
    }
    tranCommand.notes =
      TransactionConstants.HELIOS_SAVE_NOTE_CW_DIRECT_TRANSFER_EDIT
    return this.createEditTransactionService.editTransaction(
      sessionId,
      transactionId,
      tranCommand,
    )
  }

  private createCWTransaction(
    sessionId: string,
    tranCommand: CreateEditTransactionRequest,
  ) {
    tranCommand.notes =
      TransactionConstants.HELIOS_SAVE_NOTE_CW_DIRECT_TRANSFER_CREATE
    return this.createEditTransactionService.createTransaction(
      sessionId,
      tranCommand,
    )
  }

  /** @internal */
  prepareTransactionData(isValidate: boolean): DirectTransfer {
    let createdDate = new Date().toISOString()
    const unitNodeValue = this.rowNodeMap.get('units')?.data.value
    if (isEditModal(this.transInput) && this.originalTransaction?.createdDate) {
      createdDate = new Date(
        this.originalTransaction?.createdDate,
      ).toISOString()
    }
    const cp = getDefaultUnknownCounterParty()
    const transactionData: DirectTransfer = {
      units: this.updateUnit(unitNodeValue),
      tranType: this.transferType.value.tranType,
      createdDate: createdDate,
      active: true,
      userId: this.userId ?? 0,
      sourceId: TransactionConstants.HELIOS_SOURCE_ID,
      accountId: this.accountDetails?.accountId as number,
      entryDate: this.rowNodeMap.get('entryDate')?.data.value,
      settleDate: this.rowNodeMap.get('entryDate')?.data.value,
      postDate: this.rowNodeMap.get('entryDate')?.data.value,
      modifiedDate: new Date().toISOString(),
      revisionId: isEditModal(this.transInput)
        ? (this.originalTransaction?.revisionId ?? 0) + 1
        : 1,
      dataMap: this.originalTransaction?.dataMap ?? {},
    }

    if (!isValidate) {
      transactionData.securityId = this.currentSecurityId ?? -1
      transactionData.counterParty = cp
      transactionData.counterPartyId = cp.id
    }
    const collateralNode = this.rowNodeMap.get('collateralBroker')
    if (
      collateralNode &&
      collateralNode.data.value != '' &&
      transactionData.dataMap
    ) {
      transactionData.dataMap.collateralBroker = collateralNode.data.value
    }

    if (!collateralNode || collateralNode.data.value == '') {
      delete transactionData.dataMap?.collateralBroker
    }

    const prepaidInterestNode = this.rowNodeMap.get('prepaidInterest')
    if (
      prepaidInterestNode &&
      prepaidInterestNode.data.value != '' &&
      transactionData.dataMap
    ) {
      transactionData.dataMap.prepaidInterest = Number(
        prepaidInterestNode.data.value,
      )
    }

    if (!prepaidInterestNode || prepaidInterestNode.data.value == '') {
      delete transactionData.dataMap?.prepaidInterest
    }

    const interestDueNode = this.rowNodeMap.get('interestDue')
    if (
      interestDueNode &&
      interestDueNode.data.value != '' &&
      transactionData.dataMap
    ) {
      transactionData.dataMap.interestDue = Number(interestDueNode.data.value)
    }

    if (!interestDueNode || interestDueNode.data.value == '') {
      delete transactionData.dataMap?.interestDue
    }

    const exitFeeReceivableNode = this.rowNodeMap.get('exitFeeReceivable')
    if (
      exitFeeReceivableNode &&
      exitFeeReceivableNode.data.value != '' &&
      transactionData.dataMap
    ) {
      transactionData.dataMap.exitFeeReceivable = Number(
        exitFeeReceivableNode.data.value,
      )
    }

    if (!exitFeeReceivableNode || exitFeeReceivableNode.data.value == '') {
      delete transactionData.dataMap?.exitFeeReceivable
    }

    if (this.rowNodeMap.get('isConversion')?.data.value) {
      transactionData.exchangeType = ExchangeType.CONVERSION
    } else {
      transactionData.exchangeType = ExchangeType.CUSIP_EXCHANGE
    }
    const inventoryNode = this.rowNodeMap.get('inventoryType')?.data.value
    if (inventoryNode != null) {
      transactionData.inventoryType = inventoryNode
    }

    if (this.rowNodeMap.get('transferPrice')?.data.value) {
      transactionData.transferPrice = Number(
        this.rowNodeMap.get('transferPrice')?.data.value,
      )
    }
    if (this.clearTransaction.value) {
      transactionData.clearedDate = isValidate
        ? new Date().toISOString().split('Z')[0]
        : new Date().toISOString()
    }

    if (this.originalTransaction != null) {
      transactionData.transactionId = this.originalTransaction.transactionId
      transactionData.legacyOrigTranId =
        this.originalTransaction.legacyOrigTranId
      transactionData.legacyTranId = this.originalTransaction.legacyTranId
      transactionData.basisId = this.originalTransaction?.basisId
    }
    if (!isValidate && this.originalTransaction?.typeLabelId) {
      transactionData.typeLabelId = this.originalTransaction.typeLabelId
    }
    return transactionData
  }
  updateUnit(unit: number) {
    if (this.rowNodeMap.get('inventoryType')?.data.value == 'SHORT')
      return Math.abs(unit)
    else if (this.rowNodeMap.get('inventoryType')?.data.value == 'LONG')
      return -1 * Math.abs(unit)
    else return unit
  }

  updateSleeveAccounts(sleeveAccountDetail: CombinedAccountDetails) {
    // A concrete sub-account is now resolved; clear the deferred-validation state.
    this.aggregateAccountUnresolved = false
    this.accountDetails = sleeveAccountDetail.accountDetails
    this.sourceAccountId = sleeveAccountDetail.accountDetails.accountId
    this.isSourceCollateral = sleeveAccountDetail.isCollateral
  }

  setErrorDetails(errorDetails: unknown) {
    this.errorDetails = errorDetails
  }

  setErrorHeader(errorHeader: string | undefined) {
    this.errorHeader = errorHeader
  }

  validate(event?: CellValueChangedEvent) {
    const startTime = Date.now()
    // Don't validate while an aggregate account is unresolved; there's no concrete
    // account to scope the request to yet (edited fields are flagged instead).
    if (this.aggregateAccountUnresolved) {
      return
    }
    this.isDataValid = false
    this.isValidating = true
    const validateView = this.createValidationView(
      this.accountDetails as AccountDetails,
    )
    this.createEditTransactionService
      .validateTransaction(
        validateView,
        TranCategoryUrl.DIRECT_TRANSFER + '/security',
      )
      .subscribe(
        validationInfos => {
          if (
            this.isSourceCollateral != this.isDestinationCollateral &&
            !(validateView.userData.transaction as DirectTransfer).dataMap
              ?.collateralBroker
          ) {
            validationInfos.push({
              message: 'Collateral Broker must be specified',
              level: ErrorSeverity.ERROR,
              impactedFields: ['collateralBroker'],
            })
          }

          const prepaidInterest = Number(
            this.rowNodeMap.get('prepaidInterest')?.data.value || 0,
          )
          const interestDue = Number(
            this.rowNodeMap.get('interestDue')?.data.value || 0,
          )

          if (prepaidInterest !== 0 && interestDue !== 0) {
            validationInfos.push({
              message:
                'Cannot have both Prepaid Interest and Past Interest Due. Please specify only one.',
              level: ErrorSeverity.ERROR,
              impactedFields: ['prepaidInterest', 'interestDue'],
            })
          }

          if (prepaidInterest < 0) {
            validationInfos.push({
              message: 'Prepaid Interest must be a positive value.',
              level: ErrorSeverity.ERROR,
              impactedFields: ['prepaidInterest'],
            })
          }

          if (interestDue < 0) {
            validationInfos.push({
              message: 'Past Interest Due must be a positive value.',
              level: ErrorSeverity.ERROR,
              impactedFields: ['interestDue'],
            })
          }

          this.handleValidationResponse(
            validationInfos.filter(
              validationInfo =>
                validationInfo.level != ErrorSeverity.FORBIDDEN &&
                !validationInfo.impactedFields.includes('generic'),
            ),
          )

          this.handleValidationResponseForbidden(
            validationInfos.filter(
              validationInfo => validationInfo.level == ErrorSeverity.FORBIDDEN,
            ),
          )
          if (!this.forbiddenErrors.errors) {
            this.handleValidationResponseGeneric(
              validationInfos.filter(validationInfo =>
                validationInfo.impactedFields.includes('generic'),
              ),
            )
            this.handleValidationResponseConfirmButton(
              validationInfos.filter(validationInfo =>
                validationInfo.impactedFields.includes('confirmButton'),
              ),
            )
          }
          this.isValidating = false
          if (event?.data['id'] == 'destAccountId') {
            this.loadDestinationAccountDetail(event?.data['value'])
          }
          const now = Date.now()
          const type = singleDialogMetricsDetail(this.transInput)
          this.telemetryService.emitCustomEvent(
            SINGLE_DIALOG_VALIDATION_EVENT_NAME,
            {
              dialogName: 'Direct-Transfer-' + type,
              durationMs: now - startTime,
            },
          )
        },
        err => {
          this.isValidating = false
          this.errorHeader = TransactionConstants.VALIDATION_ERROR
          this.errorDetails = err.error
        },
      )
  }

  handleValidationResponseGeneric(genericValidationInfos: ValidationInfo[]) {
    if (this.transInput?.transactionBasis) {
      this.genericWarnings.add(
        TransactionConstants.BASIS_TRANSFER_EDIT_WARNING.replace(
          '[BASIS]',
          this.transInput?.transactionBasis,
        ),
      )
    }
    genericValidationInfos.forEach(validationInfo => {
      this.genericWarnings.add(validationInfo.message)
    })
  }

  handleValidationResponseConfirmButton(validationInfos: ValidationInfo[]) {
    if (validationInfos.length == 0) {
      this.saveButtonWarning = null
      return
    }
    const saveButtonWarning: string[] = []
    validationInfos.forEach(validationInfo => {
      saveButtonWarning.push(validationInfo.message)
    })
    this.saveButtonWarning = saveButtonWarning.join()
  }

  handleValidationResponseForbidden(validationInfos: ValidationInfo[]) {
    if (validationInfos.length == 0) return
    const forbiddenErrorsList: string[] = []
    validationInfos.forEach(validationInfo => {
      forbiddenErrorsList.push(validationInfo.message)
    })
    const validatorKey = 'forbiddenErrorsValidator'
    this.forbiddenErrors.markAsTouched()
    this.forbiddenErrors.setErrors({
      [validatorKey]: {
        message: forbiddenErrorsList,
      },
    })
    this.isDataValid = false
  }

  handleValidationResponse(validationInfos: ValidationInfo[]) {
    this.loadedComponent =
      this.transferType.value.id == this.exchangeOut.id
        ? this.exchangeComponent
        : this.transferComponent
    const errorMessages: Map<string, string[]> = new Map()
    const warningMessages: Map<string, string[]> = new Map()
    validationInfos.forEach(validationInfo => {
      if (validationInfo.level == ErrorSeverity.ERROR) {
        validationInfo.impactedFields.forEach(impactedField => {
          if (errorMessages.has(impactedField)) {
            errorMessages.get(impactedField)?.push(validationInfo.message)
          } else {
            errorMessages.set(impactedField, [validationInfo.message])
          }
        })
      } else {
        validationInfo.impactedFields.forEach(impactedField => {
          if (warningMessages.has(impactedField)) {
            warningMessages.get(impactedField)?.push(validationInfo.message)
          } else {
            warningMessages.set(impactedField, [validationInfo.message])
          }
        })
      }
    })
    this.isDataValid = errorMessages.size == 0
    this.rowNodeMap.forEach((value: RowNode) => {
      this.loadedComponent?.handleValidation(value, 'VALID', '')
    })
    this.note.setErrors(null)
    errorMessages.forEach((value: string[], key: string) => {
      let currentNode = this.rowNodeMap.get(key)
      if (key == 'securityId') currentNode = this.rowNodeMap.get('oldCusip')
      if (key == 'destinationCusip')
        currentNode = this.rowNodeMap.get('newCusip')
      if (key == 'destinationCusip')
        currentNode = this.rowNodeMap.get('newCusip')
      if (key == 'accountId') currentNode = this.rowNodeMap.get('sourceAccount')
      if (currentNode) {
        this.loadedComponent?.handleValidation(
          currentNode,
          'INVALID',
          value.join(' | '),
        )
      } else if (key == 'transactionNote') {
        const validatorKey = 'transactionNoteValidator'
        this.note.markAsTouched()
        this.note.setErrors({
          [validatorKey]: {
            message: value,
          },
        })
      }
    })
    warningMessages.forEach((value: string[], key: string) => {
      let currentNode = this.rowNodeMap.get(key)
      if (key == 'securityId') currentNode = this.rowNodeMap.get('oldCusip')
      if (key == 'destinationCusip')
        currentNode = this.rowNodeMap.get('newCusip')
      if (key == 'accountId') currentNode = this.rowNodeMap.get('sourceAccount')
      if (currentNode && currentNode.data.errorMessage == '') {
        this.loadedComponent?.handleValidation(
          currentNode,
          'WARNING',
          value.join(' | '),
        )
      } else if (
        key == 'transactionNote' &&
        !errorMessages.has('transactionNote')
      ) {
        const validatorKey = 'transactionNoteValidator'
        this.noteWarning = {
          [validatorKey]: {
            message: value,
          },
        }
      }
    })
  }

  createValidationView(
    accountDetails: AccountDetails,
  ): DirectTransferValidationView {
    const view: DirectTransferValidationView = {
      userData: {
        transaction: this.prepareTransactionData(true),
        cusip: this.rowNodeMap.get('oldCusip')?.data.value,
        existingUnits:
          this.originalTransaction?.units != null
            ? this.originalTransaction.units
            : 0,
      },
      destCusip: this.rowNodeMap.get('newCusip')?.data.value,
      accountDetails: accountDetails,
      transactionBasis: this.transInput?.transactionBasis,
      requestMode: getRequestModeFromModalMode(this.transInput, false),
      windowDate: this.windowDate ?? new Date(),
      sessionId: this.sessionId ?? '',
      reconDate: this.reconDate ?? new Date(),
    }

    if (this.rowNodeMap.get('destAccountId')?.data.value) {
      view.destAccountId = Number(
        this.rowNodeMap.get('destAccountId')?.data.value,
      )
    }

    if (this.rowNodeMap.get('rate')?.data.value) {
      view.metadata = {
        unitsRatio: Number(this.rowNodeMap.get('rate')?.data.value),
      }
    }

    if (this.note.value != '') {
      view.transactionNote = this.note.value
    }
    return view
  }

  onTransferChange() {
    this.note.setValue('')
    if (this.sleeveSimpleAccount?.length > 0) {
      this.resetAggAccountDetails()
    }
    this.errorDetails = ''
    this.validate()
  }

  resetAggAccountDetails() {
    this.sourceAccountId = undefined
    this.isSourceCollateral = false
    this.accountDetails = undefined
  }
  fetchSleeveSimples(accountId: number) {
    this.errorDetails = ''
    const sleeveSimples$ = this.createEditTransactionService
      .fetchSubAccounts(accountId)
      .pipe(retry(2))
    sleeveSimples$.subscribe(
      account => {
        this.sleeveSimpleAccount = account
      },
      err => {
        this.errorHeader = TransactionConstants.ERROR_LOAD_SLEEVE_ACCOUNT
        this.errorDetails = err.error
      },
    )
  }

  disableGridFocus() {
    this.exchangeComponent?.disableGridFocus()
    this.transferComponent?.disableGridFocus()
  }

  async loadAndMapAccountDetailsForSleeve(accountId: number) {
    const accountDetailsPromise = this.createEditTransactionService
      .loadAccountDetails(accountId)
      .toPromise()
    const isCollateralBrokerPromise = this.createEditTransactionService
      .isCollateralBroker([accountId])
      .toPromise()
    Promise.all([accountDetailsPromise, isCollateralBrokerPromise]).then(
      ([accountDetails, isCollateralBrokerMap]) => {
        this.accountDetails = accountDetails
        this.sourceAccountId = accountDetails.accountId
        this.isSourceCollateral =
          isCollateralBrokerMap[this.sourceAccountId ?? 0]
      },
    )
  }
  loadDestinationAccountDetail(destinationAccountId: number) {
    this.errorDetails = ''
    this.createEditTransactionService
      .loadAccountDetails(destinationAccountId)
      .subscribe(
        destinationAccountDetail => {
          this.destinationAccountDetail = destinationAccountDetail
        },
        err => {
          this.errorHeader = TransactionConstants.ERROR_LOADING_ACCOUNT
          this.errorDetails = err.error
        },
      )

    this.createEditTransactionService
      .isCollateralBroker([destinationAccountId])
      .subscribe(
        isCollateralMap => {
          this.isDestinationCollateral = isCollateralMap[destinationAccountId]
        },
        err => {
          this.errorHeader = 'Error Loading Collateral details'
          this.errorDetails = err.error
        },
      )
  }

  editingStarted() {
    this.isCellEditing = true
  }

  editingStopped() {
    this.isCellEditing = false
  }

  @HostListener('click') dialogClicked() {
    if (isDialogComponentInActive(this.compId)) {
      activateDialog(this.activeInstances, this.compId, this.elRef)
    }
  }

  getComponentId() {
    return this.compId
  }
}
